import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Bell, Building2, LogOut, User, UserPlus, BriefcaseMedical, ShieldCheck, Stethoscope } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, ADMIN_ROLES, STAFF_ROLES, ROLE_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";

function BackToClinic() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (pathname === "/") return null;
  return (
    <button data-testid="topbar-back-clinic-btn" onClick={() => navigate("/", { state: { enterClinic: true } })} aria-label="Torna alla clinica"
      className="flex h-9 items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 text-xs font-semibold text-emerald-800 transition-colors hover:bg-emerald-100">
      <Building2 className="h-4 w-4" /><span className="hidden sm:inline">Torna alla clinica</span>
    </button>
  );
}

function NotificationsBell() {
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    const load = () => api.get("/notifications").then(({ data }) => setCount(data.filter((n) => !n.read).length)).catch(() => {});
    load();
    window.addEventListener("selvix-notification", load);
    return () => window.removeEventListener("selvix-notification", load);
  }, []);
  return (
    <button
      data-testid="notifications-bell"
      onClick={() => navigate("/area-personale?tab=notifiche")}
      className="relative grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:text-sky-700"
      aria-label="Notifiche"
    >
      <Bell className="h-4 w-4" />
      {count > 0 && (
        <span data-testid="notifications-count" className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-cyan-500 px-1 text-[10px] font-bold text-white">
          {count}
        </span>
      )}
    </button>
  );
}

export function Brand({ dark = false }) {
  return (
    <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2.5">
      <span className="grid h-9 w-9 place-items-center rounded-xl bg-sky-600 text-white shadow-lg shadow-cyan-500/30">
        <Stethoscope className="h-5 w-5" />
      </span>
      <span className="leading-tight">
        <span className={`block font-display text-sm font-extrabold tracking-[0.18em] ${dark ? "text-white" : "text-slate-900"}`}>DOCTOR SELVIX</span>
        <span className={`block text-[10px] font-semibold uppercase tracking-[0.25em] ${dark ? "text-cyan-200" : "text-cyan-700"}`}>Clinica Virtuale</span>
      </span>
    </Link>
  );
}

export function UserMenu({ compact = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const lbl = compact ? "hidden sm:inline" : "";
  if (!user) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-1.5 sm:gap-2 [&>button]:h-8 [&>button]:px-3 [&>button]:text-xs sm:[&>button]:h-10 sm:[&>button]:px-4 sm:[&>button]:text-sm">
        <Button data-testid="topbar-login-btn" onClick={() => navigate("/accedi")} className="rounded-full bg-sky-600 hover:bg-sky-700">
          <User className="mr-1.5 h-4 w-4" /> Accedi
        </Button>
        <Button data-testid="topbar-register-btn" variant="outline" onClick={() => navigate("/accedi?tab=registrati")} className="rounded-full border-sky-600 text-sky-700 hover:bg-sky-50">
          <UserPlus className="mr-1.5 h-4 w-4" /> <span className={lbl}>Registrati</span>
        </Button>
        <Button data-testid="topbar-operators-btn" onClick={() => navigate("/accedi?area=operatori")} className="rounded-full bg-rose-600 text-white hover:bg-rose-700">
          <BriefcaseMedical className="mr-1.5 h-4 w-4" /> <span className={lbl}>Area operatori</span>
        </Button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <BackToClinic />
      <NotificationsBell />
      {STAFF_ROLES.includes(user.role) && (
        <Link data-testid="topbar-staff-link" to="/staff" className="hidden rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:text-sky-700 sm:block">
          Area staff
        </Link>
      )}
      {user.role === "super_admin" && (
        <Link data-testid="topbar-superadmin-link" to="/superadmin" className="hidden items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-800 transition-colors hover:bg-sky-100 sm:flex">
          <ShieldCheck className="h-3.5 w-3.5" /> Super Admin
        </Link>
      )}
      {ADMIN_ROLES.includes(user.role) && (
        <Link data-testid="topbar-admin-link" to="/admin" className="hidden items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:text-sky-700 sm:flex">
          <ShieldCheck className="h-3.5 w-3.5" /> Admin
        </Link>
      )}
      <Link data-testid="topbar-patient-area-link" to="/area-personale" className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1 pl-1 pr-3 text-xs font-semibold text-slate-700 transition-colors hover:text-sky-700">
        <span className="grid h-7 w-7 place-items-center overflow-hidden rounded-full bg-cyan-100 text-cyan-800">
          {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : user.name?.[0]}
        </span>
        <span className="hidden max-w-28 truncate md:block">{user.name}</span>
        <span className="hidden text-[10px] font-medium text-slate-400 lg:block">{ROLE_LABELS[user.role]}</span>
      </Link>
      <button data-testid="topbar-logout-btn" onClick={async () => { navigate("/"); await logout(); }} className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors hover:text-rose-600" aria-label="Esci">
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );
}

export function PageShell({ title, subtitle, children, testid }) {
  return (
    <div className="min-h-screen bg-slate-50 grid-bg" data-testid={testid}>
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <Brand />
          <UserMenu />
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="mb-8 rise">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-700">Clinica Virtuale Doctor Selvix</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{title}</h1>
          {subtitle && <p className="mt-2 max-w-2xl text-sm text-slate-600 sm:text-base">{subtitle}</p>}
        </div>
        {children}
      </main>
    </div>
  );
}
